import { Box, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';

import { TypographyStyle } from './ProgressBar.styles';

type ProgressBarProps = {
    currentPage: number;
    currentSection: number;
    totalSections: number;
    totalPagesInASection: number;
    sectionLabel: string;
};

type FilledBarProps = {
    percentage: number;
    testId?: string;
};

const FilledBar = ({ percentage, testId }: FilledBarProps) => {
    return (
        <Box
            sx={(theme) => ({
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
                overflow: 'hidden',
                borderRadius: '0px',
                backgroundColor: theme.palette.grey[300],
            })}
            data-testid={testId}>
            <Box
                sx={(theme) => ({
                    height: 8,
                    width: percentage + '%',
                    backgroundColor: theme.palette.primary.main,
                    transition: 'background-color 0.3s ease-in-out',
                })}
            />
        </Box>
    );
};

const ProgressBar = (props: ProgressBarProps) => {
    const {
        currentPage: currentPageIndex,
        currentSection: currentSectionIndex,
        totalSections,
        totalPagesInASection,
        sectionLabel,
    } = props;

    // Make sure the current page can not exceed the total pages in section
    const currentPageNumber = currentPageIndex + 1;
    const pageCount = `(${Math.min(currentPageNumber, totalPagesInASection)}/${totalPagesInASection})`;

    const listSections: string[] = useMemo(() => {
        return Array.from({ length: totalSections }, (_, i) => i.toString());
    }, [totalSections]);

    return (
        <Stack spacing={2} sx={{ mb: 3 }}>
            <Stack direction={'row'} spacing={2}>
                {listSections.map((_, index) => (
                    <Typography
                        variant="body1"
                        key={'sectionTitle' + index}
                        sx={TypographyStyle}
                        data-testid={'sectionTitle' + index}>
                        {currentSectionIndex === index && (
                            <>
                                {sectionLabel}{' '}
                                {totalPagesInASection > 1 && <span style={{ fontWeight: 'normal' }}>{pageCount}</span>}
                            </>
                        )}
                    </Typography>
                ))}
            </Stack>

            <Stack direction={'row'} spacing={1} width="100%">
                {listSections.map((_, index) => {
                    if (currentSectionIndex > index) {
                        return (
                            <FilledBar
                                key={'filledSection' + index}
                                percentage={100}
                                testId={'filledSection' + index}
                            />
                        );
                    }
                    if (currentSectionIndex === index) {
                        return (
                            <FilledBar
                                key={'filledSectionInASection' + index}
                                percentage={
                                    (Math.min(currentPageIndex + 1, totalPagesInASection) / totalPagesInASection) * 100
                                }
                                testId={'filledSectionInASection' + index}
                            />
                        );
                    }
                    if (currentSectionIndex < index) {
                        return (
                            <FilledBar
                                key={'unFilledSection' + index}
                                percentage={0}
                                testId={'unFilledSection' + index}
                            />
                        );
                    }
                })}
            </Stack>
        </Stack>
    );
};

export default ProgressBar;
